// THE CLIENT'S PRIMARY CONTACT — one record (Natalie / Philip 2026-10-28, docs/client-primary-contact-plan.md).
//
// The primary contact is a client_contacts person flagged 'primary' in client_contact_roles — what the client screen's
// Client Contacts card and ContactModal show. Add / Edit Client read and write that same person, so an edit in either
// place shows in both. The old clients.primary_contact_* columns are no longer written or read (they stay, unchanged,
// until a later check confirms every value made it across). One primary per client is enforced in the database
// (2026-10-28_client_primary_contact_apply.sql).
import { supabase } from '../supabase';

export const PRIMARY_FIELDS = ['first_name', 'last_name', 'title', 'email', 'phone'];
const norm = (v) => (v == null ? '' : String(v).trim());
// A field's value as compared and saved: emails lowercase (Philip 2026-10-28 — the database lowercases them on save too).
const val = (k, v) => (k === 'email' ? norm(v).toLowerCase() : norm(v));

// The client's primary contact, or null. Throws on a read error (never "none" for a failed read).
export async function loadPrimaryContact(clientId) {
  const { data, error } = await supabase.from('client_contact_roles')
    .select('contact:client_contacts(id, first_name, last_name, title, email, phone)')
    .eq('client_id', clientId).eq('role', 'primary');
  if (error) throw error;
  const found = (data || []).map((r) => r.contact).filter(Boolean);
  if (found.length > 1) throw new Error('This client has more than one primary contact — fix it on the client screen first.');
  return found[0] || null;
}

// A contact (or null) → the form's fields ('' for empty).
export const primaryToForm = (contact) => Object.fromEntries(PRIMARY_FIELDS.map((k) => [k, val(k, contact && contact[k])]));

// "Jane Q. Smith" → { first_name: 'Jane Q.', last_name: 'Smith' } (Admin's bulk import, which has one name column).
export function splitName(full) {
  const parts = norm(full).split(/\s+/).filter(Boolean);
  if (parts.length < 2) return { first_name: parts[0] || '', last_name: '' };
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] };
}

// What stops these fields saving, or null. All empty = no primary contact; otherwise first, last and email, as
// ContactModal requires.
export function primaryProblem(form) {
  const f = primaryToForm(form);
  if (PRIMARY_FIELDS.every((k) => !f[k])) return null;
  if (!f.first_name || !f.last_name || !f.email) return 'Primary contact: first name, last name and email are required (or leave all of its fields empty).';
  return null;
}

export const primaryChanged = (current, form) => PRIMARY_FIELDS.some((k) => val(k, current && current[k]) !== val(k, form[k]));

// Save the form's primary contact for the client. current: the primary as loaded (or null).
//   no primary, fields empty      → nothing
//   primary,    fields emptied    → the Primary flag comes off (the person stays on the client screen)
//   primary,    fields changed    → that person is updated (changed fields only)
//   no primary, fields filled     → the client's contact with the same email becomes primary (updated), else a new one
// Every write checks its row count (RLS-blocked writes return 0 rows and no error). → an error message, or null.
export async function savePrimaryContact(clientId, current, form) {
  const problem = primaryProblem(form);
  if (problem) return problem;
  const f = primaryToForm(form);
  const empty = PRIMARY_FIELDS.every((k) => !f[k]);
  const values = Object.fromEntries(PRIMARY_FIELDS.map((k) => [k, f[k] || null]));
  const changedFrom = (row) => Object.fromEntries(PRIMARY_FIELDS.filter((k) => val(k, row[k]) !== f[k]).map((k) => [k, values[k]]));
  const one = (res, what) => (res.error ? `${what}: ${res.error.message}` : (res.data || []).length !== 1 ? `${what}: not saved (0 rows — blocked?)` : null);

  if (!current && empty) return null;
  if (current && empty) {
    return one(await supabase.from('client_contact_roles').delete().eq('contact_id', current.id).eq('role', 'primary').select('id'), 'Primary contact');
  }
  if (current) {
    const patch = changedFrom(current);
    if (!Object.keys(patch).length) return null;
    return one(await supabase.from('client_contacts').update(patch).eq('id', current.id).select('id'), 'Primary contact');
  }
  // A new primary: reuse the client's contact with this email rather than add a second copy of the same person.
  const { data: same, error } = await supabase.from('client_contacts').select('id, first_name, last_name, title, email, phone')
    .eq('client_id', clientId).ilike('email', f.email.replace(/[\\%_]/g, '\\$&'));   // case-insensitive, exact: _ and % escaped
  if (error) return `Primary contact: ${error.message}`;
  let id;
  if ((same || []).length) {
    id = same[0].id;
    const patch = changedFrom(same[0]);
    if (Object.keys(patch).length) {
      const e = one(await supabase.from('client_contacts').update(patch).eq('id', id).select('id'), 'Primary contact');
      if (e) return e;
    }
  } else {
    const res = await supabase.from('client_contacts').insert({ client_id: clientId, ...values }).select('id');
    const e = one(res, 'Primary contact');
    if (e) return e;
    id = res.data[0].id;
  }
  return one(await supabase.from('client_contact_roles').insert({ contact_id: id, client_id: clientId, role: 'primary' }).select('id'), 'Primary contact flag');
}
